import React from 'react';

import { QRCodeSVG } from '../QRCode';

/**
 * @typedef {Object} QRCodeDisplayerProps
 * @property {string} url - The URL to encode in the QR code
 * @property {string} [title] - The title of the QR code
 * @property {number} [size=200] - The size of the QR code
 * @property {string} [bgColor='#ffffff'] - The background color of the QR code
 * @property {string} [fgColor='#000000'] - The foreground color of the QR code
 * @property {'L' | 'M' | 'Q' | 'H'} [level='M'] - The error correction level of the QR code
 * @property {string} [logoImage] - The image to display in the center of the QR code
 * @property {number} [logoWidth=50] - The width of the logo image
 * @property {number} [logoHeight=50] - The height of the logo image
 * @property {React.ReactNode} [loadingPlaceholder] - Custom loading placeholder component
 */

const DefaultLoadingPlaceholder = ({ size }) => (
  <div
    style={{
      width: size,
      height: size,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f3f4f6',
      borderRadius: '4px',
    }}
  >
    <span style={{ color: '#6b7280', fontSize: '14px' }}>Loading...</span>
  </div>
);

/**
 * @param {QRCodeDisplayerProps} props
 * @returns {React.ReactNode}
 */
export const QRCodeDisplayer = ({
  url,
  size = 200,
  bgColor = '#ffffff',
  fgColor = '#000000',
  level = 'M',
  logoImage,
  logoWidth = 50,
  logoHeight = 50,
  loadingPlaceholder,
}) => {
  if (!url) {
    return (
      <div
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {loadingPlaceholder || <DefaultLoadingPlaceholder size={size} />}
      </div>
    );
  }

  const imageSettings = logoImage
    ? {
        src: logoImage,
        x: undefined,
        y: undefined,
        height: logoHeight,
        width: logoWidth,
        excavate: true,
      }
    : undefined;

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <QRCodeSVG
        size={size}
        value={url}
        level={level}
        bgColor={bgColor}
        fgColor={fgColor}
        className="rounded-md"
        imageSettings={imageSettings}
      />
    </div>
  );
};
